<template>
  <div
    class="w-full h-full bg-slate-50/70 dark:bg-[#0B1120] rounded-2xl sm:rounded-3xl p-5 sm:p-7 border border-slate-200/80 dark:border-[#263449]"
  >
    <!-- Header -->
    <div class="mb-6">
      <div
        class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4"
      >
        <!-- Title -->
        <div class="flex items-center gap-2.5">
          <div
            class="w-8 h-8 rounded-xl bg-indigo-50 dark:bg-indigo-950/60 flex items-center justify-center text-indigo-600 dark:text-indigo-400"
          >
            <BarChart3 class="w-4 h-4" />
          </div>
          <div>
            <h2 class="text-xs sm:text-sm font-bold text-slate-800 dark:text-white uppercase tracking-wider">
              Student Attendance Analytics
            </h2>
            <p class="text-[11px] text-slate-400 dark:text-slate-500 font-normal">
              Monthly breakdown and presence percentages
            </p>
          </div>
        </div>

        <!-- Right Side Filters -->
        <div
          class="flex flex-wrap items-center justify-start lg:justify-end gap-2.5"
        >
          <!-- Date Range Dropdown -->
          <div class="relative w-full sm:w-44">
            <select
              v-model="dateRangeOption"
              @change="handleDateRangeChange"
              class="w-full px-3.5 py-2 bg-white dark:bg-[#18243A] border border-slate-300 dark:border-[#263449] rounded-xl text-xs font-semibold text-slate-900 dark:text-white shadow-2xs focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
            >
              <option
                v-for="option in dateRangeOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>

          <!-- Year (Only for Full Year & Custom Range) -->
          <div
            v-if="dateRangeOption === 'full' || dateRangeOption === 'custom'"
            class="flex items-center gap-2 bg-white dark:bg-[#18243A] px-3.5 py-1.5 rounded-xl border border-slate-300 dark:border-[#263449] shadow-2xs"
          >
            <label class="text-xs font-medium text-slate-500 dark:text-slate-400">Year:</label>
            <input
              v-model.number="selectedYearLocal"
              type="number"
              class="w-16 text-xs font-bold text-slate-800 dark:text-white border-none focus:outline-none focus:ring-0 bg-transparent text-center"
            />
          </div>

          <!-- Custom Range (Appears Inline) -->
          <div
            v-if="dateRangeOption === 'custom'"
            class="flex items-center gap-2 w-full sm:w-auto"
          >
            <input
              v-model="startDate"
              type="date"
              class="flex-1 min-w-0 sm:w-36 px-3 py-2 min-h-[42px] border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-semibold bg-white dark:bg-[#18243A] text-slate-900 dark:text-white shadow-2xs focus:ring-2 focus:ring-indigo-500/20 focus:outline-none transition"
            />
            <span class="text-slate-400 font-bold text-xs shrink-0">to</span>
            <input
              v-model="endDate"
              type="date"
              class="flex-1 min-w-0 sm:w-36 px-3 py-2 min-h-[42px] border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-semibold bg-white dark:bg-[#18243A] text-slate-900 dark:text-white shadow-2xs focus:ring-2 focus:ring-indigo-500/20 focus:outline-none transition"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Summary Cards (Matching Reference Image) -->
    <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
      <!-- Present -->
      <div
        class="bg-white dark:bg-[#111C30] rounded-xl sm:rounded-2xl p-4 border border-slate-200/90 dark:border-[#263449] shadow-xs flex flex-col justify-between"
      >
        <div>
          <div class="flex items-center justify-between mb-2">
            <div class="p-2 bg-emerald-50 dark:bg-emerald-950/40 rounded-xl text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 class="w-5 h-5" />
            </div>
            <span class="text-[11px] font-medium bg-slate-100 dark:bg-[#18243A] text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-md">
              Present
            </span>
          </div>
          <h4 class="text-sm font-bold text-slate-800 dark:text-slate-100">
            Present Days
          </h4>
          <p class="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white mt-1 tracking-tight">
            {{ attendanceData?.summary?.present ?? 0 }}
          </p>
        </div>
        <p class="text-xs text-slate-400 dark:text-slate-500 mt-2">Days attended</p>
      </div>

      <!-- Absent -->
      <div
        class="bg-white dark:bg-[#111C30] rounded-xl sm:rounded-2xl p-4 border border-slate-200/90 dark:border-[#263449] shadow-xs flex flex-col justify-between"
      >
        <div>
          <div class="flex items-center justify-between mb-2">
            <div class="p-2 bg-rose-50 dark:bg-rose-950/40 rounded-xl text-rose-600 dark:text-rose-400">
              <XCircle class="w-5 h-5" />
            </div>
            <span class="text-[11px] font-medium bg-slate-100 dark:bg-[#18243A] text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-md">
              Absent
            </span>
          </div>
          <h4 class="text-sm font-bold text-slate-800 dark:text-slate-100">
            Absent Days
          </h4>
          <p class="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white mt-1 tracking-tight">
            {{ attendanceData?.summary?.absent ?? 0 }}
          </p>
        </div>
        <p class="text-xs text-slate-400 dark:text-slate-500 mt-2">Days missed</p>
      </div>

      <!-- Late -->
      <div
        class="bg-white dark:bg-[#111C30] rounded-xl sm:rounded-2xl p-4 border border-slate-200/90 dark:border-[#263449] shadow-xs flex flex-col justify-between"
      >
        <div>
          <div class="flex items-center justify-between mb-2">
            <div class="p-2 bg-amber-50 dark:bg-amber-950/40 rounded-xl text-amber-600 dark:text-amber-400">
              <Clock class="w-5 h-5" />
            </div>
            <span class="text-[11px] font-medium bg-slate-100 dark:bg-[#18243A] text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-md">
              Late
            </span>
          </div>
          <h4 class="text-sm font-bold text-slate-800 dark:text-slate-100">
            Late Arrivals
          </h4>
          <p class="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white mt-1 tracking-tight">
            {{ attendanceData?.summary?.late ?? 0 }}
          </p>
        </div>
        <p class="text-xs text-slate-400 dark:text-slate-500 mt-2">Late occurrences</p>
      </div>

      <!-- Attendance Percentage -->
      <div
        class="bg-white dark:bg-[#111C30] rounded-xl sm:rounded-2xl p-4 border border-slate-200/90 dark:border-[#263449] shadow-xs flex flex-col justify-between"
      >
        <div>
          <div class="flex items-center justify-between mb-2">
            <div class="p-2 bg-blue-50 dark:bg-blue-950/40 rounded-xl text-blue-600 dark:text-blue-400">
              <BarChart3 class="w-5 h-5" />
            </div>
            <span class="text-[11px] font-medium bg-slate-100 dark:bg-[#18243A] text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-md">
              Rate
            </span>
          </div>
          <h4 class="text-sm font-bold text-slate-800 dark:text-slate-100">
            Avg Attendance
          </h4>
          <p class="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white mt-1 tracking-tight">
            {{ attendanceData?.summary?.attendancePercentage ?? "0%" }}
          </p>
        </div>
        <p class="text-xs text-slate-400 dark:text-slate-500 mt-2">Overall presence rate</p>
      </div>
    </div>

    <!-- Chart -->
    <div
      v-if="attendanceData?.monthlyStats"
      class="bg-white dark:bg-[#111C30] rounded-2xl p-5 border border-slate-200/80 dark:border-[#263449] shadow-xs"
    >
      <div class="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 class="text-sm font-bold text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
          <span>Monthly Attendance Distribution</span>
        </h3>
        <div class="flex items-center gap-3 text-xs">
          <span class="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
            <span class="w-2 h-2 rounded-full bg-emerald-500"></span> Present
          </span>
          <span class="inline-flex items-center gap-1.5 text-[11px] font-semibold text-rose-600 dark:text-rose-400">
            <span class="w-2 h-2 rounded-full bg-rose-500"></span> Absent
          </span>
          <span class="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-600 dark:text-amber-400">
            <span class="w-2 h-2 rounded-full bg-amber-500"></span> Late
          </span>
        </div>
      </div>
      <div class="relative h-64 sm:h-72 w-full">
        <canvas ref="chartRef"></canvas>
      </div>
    </div>

    <!-- Empty -->
    <div
      v-else
      class="flex flex-col items-center justify-center py-12 bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/80 dark:border-[#263449]"
    >
      <div class="w-12 h-12 rounded-2xl bg-indigo-50 dark:bg-indigo-950/50 flex items-center justify-center text-indigo-500 mb-3">
        <BarChart3 class="w-6 h-6" />
      </div>
      <p class="text-slate-600 dark:text-slate-300 text-sm font-semibold">
        No attendance data available for display
      </p>
      <p class="text-xs text-slate-400 mt-1">
        Change the date range or year filter to load recorded attendance
      </p>
    </div>
  </div>
</template>

<script setup>
  import { ref, watch, nextTick, onMounted, onUnmounted, computed } from "vue";
  import { Chart, registerables } from "chart.js/auto";
  import { CheckCircle2, XCircle, Clock, BarChart3 } from "lucide-vue-next";

  Chart.register(...registerables);

  const props = defineProps({
    selectedYear: Number,
    attendanceData: Object,
  });

  const emit = defineEmits(["update:selectedYear", "filter-change"]);

  const selectedYearLocal = ref(props.selectedYear || new Date().getFullYear());

  watch(selectedYearLocal, (val) => {
    emit("update:selectedYear", val);
    emitFilters();
  });

  watch(
    () => props.selectedYear,
    (val) => {
      selectedYearLocal.value = val;
    },
  );

  const dateRangeOption = ref("full");
  const startDate = ref("");
  const endDate = ref("");

  const currentYear = new Date().getFullYear();
  const isCurrentYear = computed(() => selectedYearLocal.value === currentYear);

  const dateRangeOptions = computed(() => {
    if (isCurrentYear.value) {
      return [
        { label: "Full Year", value: "full" },
        { label: "Today", value: "today" },
        { label: "Yesterday", value: "yesterday" },
        { label: "Last 7 Days", value: "last7days" },
        { label: "Last 30 Days", value: "last30days" },
        { label: "Custom Range", value: "custom" },
      ];
    }

    return [
      { label: "Full Year", value: "full" },
      { label: "Custom Range", value: "custom" },
    ];
  });

  function handleDateRangeChange() {
    const today = new Date();
    const format = (d) => d.toISOString().split("T")[0];

    switch (dateRangeOption.value) {
      case "today":
        startDate.value = format(today);
        endDate.value = format(today);
        break;

      case "yesterday": {
        const y = new Date();
        y.setDate(today.getDate() - 1);
        startDate.value = format(y);
        endDate.value = format(y);
        break;
      }

      case "last7days": {
        const past7 = new Date();
        past7.setDate(today.getDate() - 6);
        startDate.value = format(past7);
        endDate.value = format(today);
        break;
      }

      case "last30days": {
        const past30 = new Date();
        past30.setDate(today.getDate() - 29);
        startDate.value = format(past30);
        endDate.value = format(today);
        break;
      }

      default:
        startDate.value = "";
        endDate.value = "";
    }

    emitFilters();
  }

  function emitFilters() {
    const formatToMonthDay = (date) => {
      if (!date) return null;
      const parts = date.split("-");
      if (parts.length === 3) {
        return `${parts[1]}-${parts[2]}`;
      }
      return date;
    };

    emit("filter-change", {
      year: selectedYearLocal.value,
      startDate: formatToMonthDay(startDate.value),
      endDate: formatToMonthDay(endDate.value),
    });
  }

  watch([startDate, endDate], () => {
    if (
      dateRangeOption.value === "custom" &&
      startDate.value &&
      endDate.value
    ) {
      emitFilters();
    }
  });

  const chartRef = ref(null);
  let chartInstance = null;

  watch(
    () => props.attendanceData,
    async (data) => {
      if (!data) return;
      await nextTick();
      if (chartInstance) {
        chartInstance.destroy();
        chartInstance = null;
      }
      renderChart(data);
    },
  );

  function renderChart(data) {
    const { monthlyStats = {}, dailyStats = {} } = data;

    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    let labels = [];
    let present = [];
    let absent = [];
    let late = [];

    if (dateRangeOption.value === "full") {
      labels = months;
      present = months.map((m) => monthlyStats[m]?.present ?? 0);
      absent = months.map((m) => monthlyStats[m]?.absent ?? 0);
      late = months.map((m) => monthlyStats[m]?.late ?? 0);
    } else {
      const dates = [];
      const start = new Date(startDate.value);
      const end = new Date(endDate.value);

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const iso = new Date(d).toISOString().split("T")[0];
        dates.push(iso);
      }

      labels = dates.map((d) =>
        new Date(d).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
      );

      present = dates.map((d) => dailyStats[d]?.present ?? 0);
      absent = dates.map((d) => dailyStats[d]?.absent ?? 0);
      late = dates.map((d) => dailyStats[d]?.late ?? 0);
    }

    if (!chartRef.value) return;
    const ctx = chartRef.value.getContext("2d");

    const isDark = document.documentElement.classList.contains("dark");
    const tickColor = isDark ? "#94A3B8" : "#64748B";
    const gridColor = isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(15, 23, 42, 0.05)";

    chartInstance = new Chart(ctx, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label: "Present",
            data: present,
            backgroundColor: "#10B981",
            borderRadius: 6,
            maxBarThickness: 32,
            stack: "attendance",
          },
          {
            label: "Absent",
            data: absent,
            backgroundColor: "#F43F5E",
            borderRadius: 6,
            maxBarThickness: 32,
            stack: "attendance",
          },
          {
            label: "Late",
            data: late,
            backgroundColor: "#F59E0B",
            borderRadius: 6,
            maxBarThickness: 32,
            stack: "attendance",
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: 750,
          easing: "easeOutQuart",
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: isDark ? "rgba(17, 28, 48, 0.95)" : "rgba(15, 23, 42, 0.95)",
            titleColor: "#FFFFFF",
            bodyColor: "#CBD5E1",
            footerColor: "#38BDF8",
            borderColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.15)",
            borderWidth: 1,
            padding: 10,
            cornerRadius: 10,
            callbacks: {
              footer: (items) => {
                const total = items.reduce((sum, item) => sum + (item.parsed.y || 0), 0);
                return `Total: ${total} days logged`;
              },
            },
          },
        },
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
            ticks: { color: tickColor, font: { size: 11, weight: "600" } },
          },
          y: {
            stacked: true,
            beginAtZero: true,
            grid: { color: gridColor, borderDash: [4, 4] },
            ticks: { color: tickColor, font: { size: 10 }, stepSize: 1 },
            border: { display: false },
          },
        },
      },
    });
  }

  onMounted(() => {
    if (props.attendanceData) {
      renderChart(props.attendanceData);
    }
  });

  onUnmounted(() => {
    if (chartInstance) chartInstance.destroy();
  });
</script>
